import React, { useState, useEffect } from "react";
import { useLocation, Link } from 'react-router-dom';
import { Button, Modal, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { error, success } from "../../../notify";
import axios from 'axios';

const ExternalWebsites = () => {
  const [websites, setWebsites] = useState([]);
  const [filteredWebsites, setFilteredWebsites] = useState([]);
  const [currentWebsite, setCurrentWebsite] = useState("");
  const [currentWebsiteId, setCurrentWebsiteId] = useState(0);
  const [websiteToEdit, setWebsiteToEdit] = useState(null);
  const [isCrawlingEnabled, setIsCrawlingEnabled] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [showClearButton, setShowClearButton] = useState(false);

  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const location = useLocation();
  const { siteId, siteName } = location.state || {};
  const addApiUrl = `${process.env.REACT_APP_SERVER_URL}/api/website/allowed_external_websites`;
  const fetchWebsitesApiUrl = `${process.env.REACT_APP_SERVER_URL}/api/website/allowed_external_websites`;
  const editWebsiteApiUrl = `${process.env.REACT_APP_SERVER_URL}/api/website/allowed_external_websites`;
  const deleteWebsiteApiUrl = `${process.env.REACT_APP_SERVER_URL}/api/website/allowed_external_websites`;

  // Search functionality - Updated to match SiteUsers.js
  const handleSearch = () => {
    if (searchInputValue.trim() !== "") {
      setShowClearButton(true);
      const filtered = websites.filter(item =>
        item.external_link.toLowerCase().includes(searchInputValue.toLowerCase())
      );
      setFilteredWebsites(filtered);
    }
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setShowClearButton(false);
    setFilteredWebsites(websites);
  };

  const handleSearchInputChange = (event) => {
    setSearchInputValue(event.target.value);
    if (event.target.value.trim() === "") {
      setShowClearButton(false);
      setFilteredWebsites(websites);
    }
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Refresh functionality
  const handleRefresh = async () => {
    try {
      const response = await axios.get(fetchWebsitesApiUrl, {
        params: { website_id: siteId },
      });
      setWebsites(response.data.websites);
      setFilteredWebsites(response.data.websites);
      success("Websites refreshed successfully.",true);
    } catch (err) {
      error(err.response?.data?.message || "Failed to refresh websites.");
    }
  };

  useEffect(() => {
    // Fetch the websites when the component mounts
    const fetchWebsites = async () => {
      try {
        const response = await axios.get(fetchWebsitesApiUrl, {
          params: { website_id: siteId },
        });
        setWebsites(response.data.websites);
        setFilteredWebsites(response.data.websites);
      } catch (err) {
        error(err.response?.data?.message || "Failed to fetch websites.");
      }
    };

    fetchWebsites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const handleCloseAddEditModal = () => {
    setCurrentWebsite("");
    setWebsiteToEdit(null);
    setShowAddEditModal(false);
  };

  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const handleAddWebsite = async () => {
    if (currentWebsite.trim() !== "") {
      try {
        const payload = {
          url: currentWebsite.trim(),
          website_id: siteId,
          enable_crawling: isCrawlingEnabled,
        };

        const response = await axios.post(addApiUrl, payload);
        const newWebsites = [...websites, response.data];
        setWebsites(newWebsites);
        setFilteredWebsites(newWebsites);
        success("Website added successfully.",true);
        handleCloseAddEditModal();
      } catch (err) {
        error(err.response?.data?.message || "Failed to add website.");
      }
    }
  };

  const handleEditWebsite = async () => {
    if (currentWebsite.trim() !== "" && websiteToEdit !== null) {
      try {
        const payload = {
          external_link: currentWebsite.trim(),
          crawlable: isCrawlingEnabled,
        };

        await axios.put(`${editWebsiteApiUrl}/${currentWebsiteId}`, payload);

        // Update the local state with the updated website data
        const updatedWebsites = websites.map((website, index) =>
          index === websiteToEdit
            ? {
                ...website,
                external_link: currentWebsite.trim(),
                enable_crawling: isCrawlingEnabled,
              }
            : website
        );

        setWebsites(updatedWebsites);
        setFilteredWebsites(updatedWebsites);
        success("Website updated successfully.",true);
        handleCloseAddEditModal();
      } catch (err) {
        error(err.response?.data?.message || "Failed to edit website.");
      }
    }
  };

  const handleDeleteWebsite = async () => {
    if (websiteToEdit !== null) {
      try {
        const websiteId = websites[websiteToEdit].external_link_id;
        const deleteUrl = `${deleteWebsiteApiUrl}/${websiteId}`;
        await axios.delete(deleteUrl);

        // Update the state to remove the deleted website
        const newWebsites = websites.filter((_, index) => index !== websiteToEdit);
        setWebsites(newWebsites);
        setFilteredWebsites(newWebsites);
        success("Website deleted successfully.",true);
      } catch (err) {
        error(err.response?.data?.message || "Failed to delete website.");
      } finally {
        handleCloseDeleteModal();
      }
    }
  };

  const handleShowAddModal = () => {
    setCurrentWebsite("");
    setIsCrawlingEnabled(false);
    setShowAddEditModal(true);
  };

  const handleShowEditModal = (index) => {
    setCurrentWebsiteId(websites[index].external_link_id);
    setCurrentWebsite(websites[index].external_link);
    setIsCrawlingEnabled(websites[index].crawlable || false);
    setWebsiteToEdit(index);
    setShowAddEditModal(true);
  };

  const handleShowDeleteModal = (index) => {
    setWebsiteToEdit(index);
    setShowDeleteModal(true);
  };

  // Icons (same as in Site.js)
  const EditIcon = () => (
    <svg width="16" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13.7476 20.4428H21.0002" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path fillRule="evenodd" clipRule="evenodd" d="M12.78 3.79479C13.5557 2.86779 14.95 2.73186 15.8962 3.49173C15.9485 3.53296 17.6295 4.83879 17.6295 4.83879C18.669 5.46719 18.992 6.80311 18.3494 7.82259C18.3153 7.87718 8.81195 19.7645 8.81195 19.7645C8.49578 20.1589 8.01583 20.3918 7.50291 20.3973L3.86353 20.443L3.04353 16.9723C2.92866 16.4843 3.04353 15.9718 3.3597 15.5773L12.78 3.79479Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.021 6.00098L16.4732 10.1881" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const DeleteIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20.708 6.23975H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a6acf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v6h6"></path>
      <path d="M21 12a9 9 0 0 0-15-6.7L3 8"></path>
      <path d="M21 22v-6h-6"></path>
      <path d="M3 12a9 9 0 0 0 15 6.7L21 16"></path>
    </svg>
  );

  const BackIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6"></polyline>
      <line x1="5" y1="6" x2="5" y2="18"></line>
    </svg>
  );

  const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );

  const ClearIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  return (
    <div style={{ 
      fontFamily: '"Inter", sans-serif', 
      background: '#f8f9fd', 
      margin: 0, 
      padding: '20px', 
      minHeight: '100vh',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <div style={{ 
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        boxSizing: 'border-box'
      }}>
        <div style={{ 
          background: '#fff', 
          borderRadius: '16px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)', 
          padding: '20px', 
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Header Section - Updated with search button and clear button */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '15px', 
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h2 style={{ fontSize: '20px', margin: 0, fontWeight: 600 }}>
              External Websites - {siteName}
            </h2>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {/* Search Box with Button - Updated to match SiteUsers.js */}
              <div style={{ position: 'relative', width: '350px', minWidth: '250px' }}>
                <input
                  type="text"
                  placeholder="Search websites..."
                  value={searchInputValue}
                  onChange={handleSearchInputChange}
                  onKeyPress={handleSearchKeyPress}
                  style={{
                    width: '100%',
                    padding: '8px 68px 8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  right: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center'
                }}>
                  {showClearButton && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleClearSearch}
                      style={{
                        background: '#5a6acf',
                        borderRadius: '8px',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0',
                        cursor: 'pointer',
                        color: '#5a6acf',
                        transition: 'all 0.2s ease-in-out',
                        border: '1px solid #ddd',
                        backgroundColor: 'white',
                        boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#5a6acf';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.color = '#5a6acf';
                      }}
                    >
                      <ClearIcon />
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    style={{
                      background: '#5a6acf',
                      borderRadius: '8px',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0',
                      cursor: 'pointer',
                      color: '#5a6acf',
                      transition: 'all 0.2s ease-in-out',
                      border: '1px solid #ddd',
                      backgroundColor: 'white',
                      boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#5a6acf';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.color = '#5a6acf';
                    }}
                  >
                    <SearchIcon />
                  </Button>
                </div>
              </div>

              <Button
                variant="outline-secondary"
                onClick={handleShowAddModal}
                style={{
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  fontSize: '14px',
                  color: '#5a6acf',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                + Add Website
              </Button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button
                variant="outline-secondary"
                onClick={handleRefresh}
                style={{
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0',
                  fontSize: '14px',
                  color: '#5a6acf',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <RefreshIcon />
              </Button>
              <Button
                variant="outline-secondary"
                style={{
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0',
                  fontSize: '14px',
                  color: '#5a6acf',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <Link
                  to="/site"
                  style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <BackIcon />
                </Link>
              </Button>
            </div>
          </div>

          {/* Table Section - Wrapped in scrollable container */}
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              minWidth: '600px' 
            }}>
              <thead>
                <tr style={{ textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#555' }}>
                  <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '45%' }}>Website URL</th>
                  <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'left', width: '40%' }}>Crawling Enabled</th>
                  <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', width: '20%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWebsites.map((website, index) => (
                  <tr key={index}>
                    <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>
                      {website.external_link}
                    </td>
                    <td style={{ padding: '14px 50px', borderBottom: '1px solid #f0f0f0', textAlign: 'left' }}>
                      {website.crawlable ? 'Yes' : 'No'}
                    </td>
                    <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', alignItems: 'center', color: '#555' }}>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                          <span
                            onClick={() => handleShowEditModal(index)}
                            style={{ cursor: 'pointer', display: 'inline-flex' }}
                          >
                            <EditIcon />
                          </span>
                        </OverlayTrigger>

                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                          <span
                            onClick={() => handleShowDeleteModal(index)}
                            style={{ cursor: 'pointer', display: 'inline-flex' }}
                          >
                            <DeleteIcon />
                          </span>
                        </OverlayTrigger>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty state */}
          {filteredWebsites.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              {websites.length === 0 ? 'No external websites added yet.' : 'No websites found matching your search.'}
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <Modal show={showAddEditModal} onHide={handleCloseAddEditModal}>
          <Modal.Header closeButton>
            <Modal.Title>{websiteToEdit !== null ? "Edit Website" : "Add Website"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Website URL</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="Enter website URL"
                  value={currentWebsite}
                  onChange={(e) => setCurrentWebsite(e.target.value)}
                />
              </Form.Group>
              <Form.Check
                type="checkbox"
                label="Enable Crawling"
                checked={isCrawlingEnabled}
                onChange={() => setIsCrawlingEnabled(!isCrawlingEnabled)}
              />
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={handleCloseAddEditModal}
              style={{ border: '1px solid #ddd', minWidth: '100px' }}
            >
              Close
            </Button>
            <Button
              variant="primary"
              onClick={websiteToEdit !== null ? handleEditWebsite : handleAddWebsite}
              style={{
                backgroundColor: '#5a6acf',
                borderColor: '#5a6acf',
              }}
            >
              {websiteToEdit !== null ? "Save Changes" : "Add Website"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to delete this website?</Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={handleCloseDeleteModal}
              style={{ border: '1px solid #ddd',  minWidth: '100px'  }}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteWebsite}
              style={{minWidth: '100px' }}
            >
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default ExternalWebsites;